import { useQueryClient } from "@tanstack/react-query";
import { ListChecks, Send } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router";

import { postFeedback, uploadFeedbackAttachment } from "@/api/feedback";
import { useAuth } from "@/auth/authState";
import { Button } from "@/components/ui/Button";
import { contextLabel } from "@/feedback/contextLabels";

import { FotoKiezer } from "./Bijlagen";
import { fotosUitPlakken } from "./fotos";
import { useFeedbackContextStore } from "./context";
import "./FeedbackButton.css";

/** Wat de gebruiker terugziet nadat hij op versturen heeft gedrukt. */
type State = "idle" | "sending" | "sent" | "failed";

/**
 * Het feedbackformulier: tekst, foto's en de context die meegaat.
 *
 * Staat in het paneel achter de knop in de kop (`FeedbackButton`) en, sinds
 * stijl A (scherm 34), ook naast de werkvoorraad op het feedbackscherm. Wat er
 * wordt meegestuurd staat onder het invoerveld, zodat er niets ongemerkt
 * meegaat. Na het versturen wordt de werkvoorraad opnieuw opgehaald: wie op
 * het feedbackscherm staat, ziet zijn opmerking er meteen bij.
 */
export function FeedbackForm({
  id,
  onNavigate,
  onSent,
}: {
  id: string;
  /** Na een klik op een link in het formulier: het paneel sluit dan. */
  onNavigate?: () => void;
  /** De opmerking is opgeslagen. */
  onSent?: () => void;
}) {
  const [message, setMessage] = useState("");
  const [state, setState] = useState<State>("idle");
  /** Het ticketnummer van de laatst verstuurde opmerking. */
  const [ticket, setTicket] = useState<string | null>(null);
  /** Foto's die met de opmerking meegaan (Tom, 15-09-2026). */
  const [fotos, setFotos] = useState<File[]>([]);
  /** Hoeveel foto's niet zijn opgeslagen, als de opmerking zelf wel is gelukt. */
  const [fotosMislukt, setFotosMislukt] = useState(0);
  const location = useLocation();
  const queryClient = useQueryClient();
  const context = useFeedbackContextStore((store) => store.context);
  const { account } = useAuth();
  const contextEntries = Object.entries(context).filter(([, value]) => value != null);

  const send = async () => {
    if (!message.trim()) return;
    setState("sending");
    try {
      const opgeslagen = await postFeedback({
        message: message.trim(),
        screen: location.pathname,
        url: window.location.href,
        author_name: account?.displayName,
        context,
      });
      setTicket(opgeslagen.ticket);
      // De opmerking staat; een foto die daarna mislukt, maakt dat niet ongedaan.
      let mislukt = 0;
      for (const foto of fotos) {
        try {
          await uploadFeedbackAttachment(opgeslagen.ticket, foto);
        } catch {
          mislukt += 1;
        }
      }
      setFotosMislukt(mislukt);
      setFotos([]);
      setMessage("");
      setState("sent");
      onSent?.();
      void queryClient.invalidateQueries({ queryKey: ["feedback"] });
    } catch {
      setState("failed");
    }
  };

  return (
    <div className="atl-feedback__form">
      {state === "sent" ? (
        <div className="atl-feedback__done">
          <p>
            Dank je. De opmerking staat vast bij dit scherm
            {ticket ? <> als <strong>{ticket}</strong></> : null}.
          </p>
          {fotosMislukt > 0 && ticket && (
            <p className="atl-feedback__error" role="status">
              {fotosMislukt === 1 ? "Eén foto is" : `${fotosMislukt} foto's zijn`} niet opgeslagen.
              Voeg ze toe op <Link to={`/feedback/${ticket}`} onClick={onNavigate}>het ticket</Link>.
            </p>
          )}
          <Button variant="secondary" size="sm" onClick={() => setState("idle")}>
            Nog een opmerking
          </Button>
        </div>
      ) : (
        <>
          <textarea
            className="atl-feedback__text"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={5}
            maxLength={5000}
            placeholder="Bijvoorbeeld: dit cijfer wijkt af van wat wij in de dPi hebben staan."
            aria-label="Je opmerking"
            onPaste={(event) => {
              const geplakt = fotosUitPlakken(event);
              if (geplakt.length > 0) setFotos((huidig) => [...huidig, ...geplakt]);
            }}
          />
          <FotoKiezer id={id} bestanden={fotos} onChange={setFotos} />

          <p className="atl-feedback__context">
            Meegestuurd: <code>{location.pathname}</code>
            {contextEntries.map(([key, value]) => (
              <span key={key} title={key}>
                {" · "}
                {contextLabel(key)}
                {contextLabel(key) === key ? ": " : " "}
                <code>{String(value)}</code>
              </span>
            ))}
          </p>

          {state === "failed" && (
            <p className="atl-feedback__error" role="status">
              Versturen lukte niet. Probeer het opnieuw; de tekst blijft staan.
            </p>
          )}

          <Button
            variant="primary"
            className="atl-feedback__send"
            onClick={send}
            disabled={state === "sending" || !message.trim()}
            icon={<Send size={14} strokeWidth={2} aria-hidden />}
          >
            {state === "sending" ? "Versturen…" : "Versturen"}
          </Button>
        </>
      )}

      {/* De weg terug. Zonder deze regel is versturen een eenrichtingsweg:
          de opmerking verdwijnt in de tabel en de inzender hoort er nooit
          meer iets over. Een meelezer ziet daar zijn eigen opmerkingen met
          de stand erbij; een schrijfrol ziet de hele werkvoorraad. Op het
          feedbackscherm zelf staat hij er niet: daar ben je al. */}
      {location.pathname !== "/feedback" && (
        <Link to="/feedback" className="atl-feedback__overzicht" onClick={onNavigate}>
          <ListChecks size={14} strokeWidth={2} aria-hidden />
          Bekijk eerdere opmerkingen en hun status
        </Link>
      )}
    </div>
  );
}
